// Stems on the producer's machine → a pending seed with a plan per object.
//
// Converts what needs converting, builds the Snapshot, and hands both to the
// shared publish steps (shared/upload/publish-seed.ts) — the ones the
// Audiotool import runs. What comes back is an open session: the hook sends
// its jobs one by one so each file shows its own progress and a failed one is
// retried alone.

import type { SeedbedTransport, SeedUploadSession, UploadJob } from "@shared/upload/publish-seed.js";
import { openSeedUpload, prepareSeed } from "@shared/upload/publish-seed.js";
import { encodePreviews } from "@upload-core/preview-encode.js";

import type { IntakeFile } from "./intake";
import { limitProblem } from "./limits";
import type { AudioProbe } from "./probe";
import { stemsSnapshot } from "./stems-snapshot";
import { toUploadBytes } from "./to-wav";

export interface StemToSend {
  file: IntakeFile;
  /** As the producer named the track. */
  name: string;
  probe: AudioProbe;
}

export interface StemsUploadInput {
  stems: StemToSend[];
  title: string;
  bpm: number;
  key: string;
  /** Remix: the seed this grows from. */
  parentSeedId: string | null;
}

export type PrepareStage = "converting" | "mixing" | "encoding" | "opening";

/** Thrown with a sentence the producer can read. */
export class UploadProblem extends Error {}

export async function openStemsUpload(
  transport: SeedbedTransport,
  input: StemsUploadInput,
  onStage: (stage: PrepareStage, done?: number, total?: number) => void,
): Promise<SeedUploadSession> {
  const clips: Array<{ trackIndex: number; clipIndex: number; bytes: Uint8Array }> = [];
  for (const [i, stem] of input.stems.entries()) {
    onStage("converting", i, input.stems.length);
    let bytes: Uint8Array;
    try {
      bytes = await toUploadBytes(stem.file.blob, stem.file.ext, stem.probe);
    } catch {
      throw new UploadProblem(`"${stem.name}" couldn't be read as audio.`);
    }
    // The probe estimated; the real bytes decide.
    const problem = limitProblem([{ name: stem.name, uploadBytes: bytes.byteLength }]);
    if (problem) throw new UploadProblem(problem);
    clips.push({ trackIndex: i, clipIndex: 0, bytes });
  }
  const total = limitProblem(clips.map((c, i) => ({ name: input.stems[i]!.name, uploadBytes: c.bytes.byteLength })));
  if (total) throw new UploadProblem(total);

  const snapshot = stemsSnapshot(
    input.stems.map((s) => ({
      name: s.name,
      durationSec: s.probe.durationSec,
      source: { format: s.probe.format, bitDepth: s.probe.bitDepth },
    })),
    input.bpm,
  );
  const prepared = await prepareSeed(
    {
      meta: {
        title: input.title,
        bpm: input.bpm,
        key: input.key,
        parentSeedId: input.parentSeedId,
        note: null,
        origin: "web_upload",
      },
      snapshot,
      clips,
      master: null,
    },
    encodePreviews,
    (stage) => onStage(stage),
  );
  onStage("opening");
  return openSeedUpload(transport, prepared);
}

/** What a job is, in the producer's words: the track(s) it carries, or the mix. */
export function jobLabel(job: UploadJob, session: SeedUploadSession, trackNames: string[]): string {
  if (job.kind === "master") return "Mix";
  if (job.kind === "preview") return "Preview";
  const hash = job.plan.hash;
  const names = session.prepared.placements.filter((p) => p.hash === hash).map((p) => trackNames[p.trackIndex] ?? "");
  return names.filter((n) => n.length > 0).join(", ") || job.label;
}
